import { EntryEditError, imageTypeOf } from '../edits.ts';
import { javaIsBlank } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection, type SqlRow } from '../sql.ts';

/**
 * The Idea Board (docs/PARITY-STEP-4-PLAN.md section 3), read and written as the
 * desktop's `BoardRepository` and `BoardService` do:
 * - boards by name, without regard to case, each with how many objects it holds;
 *   a blank name is "Untitled Board";
 * - a board's objects by their stacking order, then by when they were made: notes, cards
 *   (linked to an entry, a map and an event), pictures, shapes, and arrows between two
 *   other objects;
 * - a new object is placed a little down and right of the last, in a cascade of six;
 * - deleting a board deletes its objects, and deleting an object deletes the arrows to and
 *   from it (the schema's cascades); a deleted entry, map or event leaves its cards unlinked.
 */

export const BOARD_OBJECT_KINDS = ['TEXT', 'CARD', 'IMAGE', 'SHAPE', 'ARROW'] as const;
export type BoardObjectKind = (typeof BOARD_OBJECT_KINDS)[number];
export const SHAPE_KINDS = ['RECTANGLE', 'ELLIPSE'] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

export const BOARD_LIMITS = {
  name: 200,
  text: 20_000,
  /** A board's picture, as the desktop's `BoardService.MAX_IMAGE_BYTES`. */
  imageBytes: 50 * 1024 * 1024,
  /** How far from the middle an object may be placed, in board units. */
  coordinate: 1_000_000,
} as const;

/** The picture types a board keeps, as the desktop's `ImageDecoding.guessMimeType` names them. GIF and BMP are converted to PNG first. */
export const BOARD_IMAGE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp']);

export interface BoardSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly objectCount: number;
  readonly created: string;
  readonly modified: string;
}

export interface IdeaBoard {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly created: string;
  readonly modified: string;
}

export interface BoardObject {
  readonly id: string;
  readonly boardId: string;
  readonly kind: BoardObjectKind;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly zIndex: number;
  /** A note's text, a card's title. */
  readonly text: string;
  /** A card's notes. */
  readonly notes: string;
  readonly colorHex: string;
  readonly shapeKind: ShapeKind | null;
  readonly imageMimeType: string;
  readonly imageData: Uint8Array | null;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
  readonly linkedEventId: string | null;
  /** An arrow's ends. */
  readonly fromObjectId: string | null;
  readonly toObjectId: string | null;
  readonly created: string;
  readonly modified: string;
}

// ---- reading ----

export function listBoards(conn: SqlConnection): BoardSummary[] {
  return conn
    .all(
      `SELECT b.id, b.name, b.description, b.created, b.modified,
         (SELECT COUNT(*) FROM board_objects o WHERE o.board_id = b.id) AS object_count
       FROM idea_boards b ORDER BY b.name COLLATE NOCASE`,
    )
    .map((r) => ({ ...boardOf(r), objectCount: int(r['object_count']) }));
}

export function getBoard(conn: SqlConnection, id: string): IdeaBoard | null {
  const row = conn.all('SELECT id, name, description, created, modified FROM idea_boards WHERE id = ?', [id])[0];
  return row ? boardOf(row) : null;
}

const OBJECT_COLUMNS =
  'id, board_id, kind, x, y, width, height, z_index, text, notes, color_hex, shape_kind, image_mime_type, image_data, linked_entry_id, linked_map_id, linked_event_id, from_object_id, to_object_id, created, modified';

export function listBoardObjects(conn: SqlConnection, boardId: string): BoardObject[] {
  return conn.all(`SELECT ${OBJECT_COLUMNS} FROM board_objects WHERE board_id = ? ORDER BY z_index, created`, [boardId]).flatMap((r) => {
    const o = objectOf(r);
    return o ? [o] : [];
  });
}

/** The maps a card can link to, by name (`AtlasService.listMapSummaries`), without their pictures. */
export function listMapNames(conn: SqlConnection): { readonly id: string; readonly name: string }[] {
  return conn.all('SELECT id, name FROM atlas_maps ORDER BY name COLLATE NOCASE').map((r) => ({ id: text(r['id']), name: text(r['name']) }));
}

// ---- boards ----

export interface BoardDetails {
  readonly name: string;
  readonly description: string;
}

export function createBoard(conn: SqlConnection, details: BoardDetails, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = cleanBoard(details);
  conn.run('INSERT INTO idea_boards (name, description, created, modified, id) VALUES (?, ?, ?, ?, ?)', [clean.name, clean.description, now, now, id]);
  return id;
}

export function updateBoard(conn: SqlConnection, id: string, details: BoardDetails, now: string = new Date().toISOString()): void {
  const clean = cleanBoard(details);
  transaction(conn, () => {
    mustExist(conn, 'idea_boards', id, 'That board no longer exists.');
    conn.run('UPDATE idea_boards SET name = ?, description = ?, modified = ? WHERE id = ?', [clean.name, clean.description, now, id]);
  });
}

/** Deletes a board and everything on it; returns how many objects went with it. */
export function deleteBoard(conn: SqlConnection, id: string): number {
  return transaction(conn, () => {
    mustExist(conn, 'idea_boards', id, 'That board no longer exists.');
    const objects = int(scalar(conn, 'SELECT COUNT(*) FROM board_objects WHERE board_id = ?', [id]));
    conn.run('DELETE FROM idea_boards WHERE id = ?', [id]);
    return objects;
  });
}

// ---- objects ----

/** What a new object starts as (`BoardObject.createText`, `createCard`, `createShape`, `createImage`). */
const NEW_OBJECTS = {
  TEXT: { width: 180, height: 80, text: 'New note', color: '#F5F2EE' },
  CARD: { width: 180, height: 100, text: 'New card', color: '#E9E1F5' },
  SHAPE: { width: 140, height: 100, text: '', color: '#DCD5C9' },
  IMAGE: { width: 220, height: 160, text: '', color: '#FFFFFF' },
} as const;
const ARROW_COLOR = '#2B2140';

export type NewBoardObject =
  | { readonly kind: 'TEXT' }
  | { readonly kind: 'CARD' }
  | { readonly kind: 'SHAPE'; readonly shapeKind: ShapeKind }
  | { readonly kind: 'IMAGE'; readonly data: Uint8Array };

/** Adds a note, card, shape or picture to a board, where `BoardPane.nextCascadeX` and `Y` place it. */
export function createBoardObject(conn: SqlConnection, boardId: string, object: NewBoardObject, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  let mime = '';
  let data: Uint8Array | null = null;
  if (object.kind === 'IMAGE') {
    const type = imageTypeOf(object.data);
    if (type === null || !BOARD_IMAGE_TYPES.has(type)) throw new EntryEditError('invalid', "That isn't a picture Realmery can use (PNG, JPEG, GIF, WebP or BMP).");
    if (object.data.byteLength > BOARD_LIMITS.imageBytes) throw new EntryEditError('invalid', 'A picture can be at most 50 MB.');
    mime = type;
    data = object.data;
  }
  if (object.kind === 'SHAPE' && !SHAPE_KINDS.includes(object.shapeKind)) throw new EntryEditError('invalid', 'That is not a shape.');
  if (!(object.kind in NEW_OBJECTS)) throw new EntryEditError('invalid', 'That is not something a board can hold.');
  const start = NEW_OBJECTS[object.kind];
  transaction(conn, () => {
    mustExist(conn, 'idea_boards', boardId, 'That board no longer exists.');
    const at = 40 + (int(scalar(conn, 'SELECT COUNT(*) FROM board_objects WHERE board_id = ?', [boardId])) % 6) * 30;
    conn.run(
      `INSERT INTO board_objects (x, y, width, height, z_index, text, notes, color_hex, shape_kind, image_mime_type, image_data,
         linked_entry_id, linked_map_id, linked_event_id, from_object_id, to_object_id, created, modified, board_id, kind, id)
       VALUES (?, ?, ?, ?, 0, ?, '', ?, ?, ?, ?, NULL, NULL, NULL, NULL, NULL, ?, ?, ?, ?, ?)`,
      [at, at, start.width, start.height, start.text, start.color, object.kind === 'SHAPE' ? object.shapeKind : null, mime, data, now, now, boardId, object.kind, id],
    );
  });
  return id;
}

/**
 * Joins two objects with an arrow (`BoardService.createArrow`): not an object to itself, nor to
 * or from an arrow. Stored 180 by 90, as the desktop's `BoardObject` turns the 0 by 0 it's given.
 */
export function createArrow(conn: SqlConnection, boardId: string, fromId: string, toId: string, id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  if (fromId === toId) throw new EntryEditError('invalid', 'An arrow cannot connect an object to itself.');
  transaction(conn, () => {
    for (const end of [fromId, toId]) {
      const row = conn.all('SELECT kind, board_id FROM board_objects WHERE id = ?', [end])[0];
      if (!row || text(row['board_id']) !== boardId) throw new EntryEditError('not_found', 'One of those objects is no longer on this board.');
      if (text(row['kind']) === 'ARROW') throw new EntryEditError('invalid', 'An arrow cannot connect to another arrow.');
    }
    conn.run(
      `INSERT INTO board_objects (x, y, width, height, z_index, text, notes, color_hex, shape_kind, image_mime_type, image_data,
         linked_entry_id, linked_map_id, linked_event_id, from_object_id, to_object_id, created, modified, board_id, kind, id)
       VALUES (0, 0, 180, 90, 0, '', '', ?, NULL, '', NULL, NULL, NULL, NULL, ?, ?, ?, ?, ?, 'ARROW', ?)`,
      [ARROW_COLOR, fromId, toId, now, now, boardId, id],
    );
  });
  return id;
}

/** Moves an object (`BoardService.moveObject`); its size stays. */
export function moveBoardObject(conn: SqlConnection, id: string, x: number, y: number, now: string = new Date().toISOString()): void {
  for (const v of [x, y]) {
    if (!Number.isFinite(v) || Math.abs(v) > BOARD_LIMITS.coordinate) throw new EntryEditError('invalid', 'That is too far off the board.');
  }
  transaction(conn, () => {
    mustExist(conn, 'board_objects', id, 'That is no longer on the board.');
    conn.run('UPDATE board_objects SET x = ?, y = ?, modified = ? WHERE id = ?', [x, y, now, id]);
  });
}

export interface BoardObjectDetails {
  readonly text: string;
  readonly notes: string;
  readonly colorHex: string;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
  readonly linkedEventId: string | null;
}

/**
 * Saves what Edit... changes, as `BoardPane.editObject` does: a note's text and colour, a
 * card's title, notes, colour and links, a shape's colour. A note or shape keeps no links.
 */
export function updateBoardObject(conn: SqlConnection, id: string, details: BoardObjectDetails, now: string = new Date().toISOString()): void {
  if (!/^#[0-9A-Fa-f]{6}$/.test(details.colorHex)) throw new EntryEditError('invalid', 'That is not a colour.');
  for (const [value, what] of [
    [details.text, 'The text'],
    [details.notes, 'The notes'],
  ] as const) {
    if (value.length > BOARD_LIMITS.text) throw new EntryEditError('invalid', `${what} can be at most ${BOARD_LIMITS.text.toLocaleString('en')} characters.`);
  }
  transaction(conn, () => {
    const row = conn.all('SELECT kind, text, notes FROM board_objects WHERE id = ?', [id])[0];
    if (!row) throw new EntryEditError('not_found', 'That is no longer on the board.');
    const kind = text(row['kind']);
    if (kind === 'ARROW' || kind === 'IMAGE') throw new EntryEditError('invalid', 'That has nothing to edit.');
    const card = kind === 'CARD';
    if (card && details.text === '') throw new EntryEditError('invalid', 'A card needs a title.');
    if (card) {
      if (details.linkedEntryId !== null) mustExist(conn, 'lore_entries', details.linkedEntryId, 'That entry no longer exists.');
      if (details.linkedMapId !== null) mustExist(conn, 'atlas_maps', details.linkedMapId, 'That map no longer exists.');
      if (details.linkedEventId !== null) mustExist(conn, 'chronology_events', details.linkedEventId, 'That event no longer exists.');
    }
    const newText = kind === 'SHAPE' ? text(row['text']) : details.text;
    const notes = card ? details.notes : text(row['notes']);
    conn.run('UPDATE board_objects SET text = ?, notes = ?, color_hex = ?, linked_entry_id = ?, linked_map_id = ?, linked_event_id = ?, modified = ? WHERE id = ?', [
      newText,
      notes,
      details.colorHex,
      card ? details.linkedEntryId : null,
      card ? details.linkedMapId : null,
      card ? details.linkedEventId : null,
      now,
      id,
    ]);
  });
}

/** Puts an object above every other on its board (`BoardService.bringToFront`). */
export function bringToFront(conn: SqlConnection, id: string, now: string = new Date().toISOString()): void {
  transaction(conn, () => {
    const row = conn.all('SELECT board_id FROM board_objects WHERE id = ?', [id])[0];
    if (!row) throw new EntryEditError('not_found', 'That is no longer on the board.');
    const top = int(scalar(conn, 'SELECT COALESCE(MAX(z_index), 0) FROM board_objects WHERE board_id = ?', [text(row['board_id'])]));
    conn.run('UPDATE board_objects SET z_index = ?, modified = ? WHERE id = ?', [top + 1, now, id]);
  });
}

/** Deletes an object, and the arrows to and from it; returns how many arrows went with it. */
export function deleteBoardObject(conn: SqlConnection, id: string): number {
  return transaction(conn, () => {
    mustExist(conn, 'board_objects', id, 'That is no longer on the board.');
    const arrows = int(scalar(conn, 'SELECT COUNT(*) FROM board_objects WHERE from_object_id = ? OR to_object_id = ?', [id, id]));
    conn.run('DELETE FROM board_objects WHERE id = ?', [id]);
    return arrows;
  });
}

// ---- helpers ----

function boardOf(r: SqlRow): IdeaBoard {
  const name = text(r['name']);
  return { id: text(r['id']), name: javaIsBlank(name) ? 'Untitled Board' : name, description: text(r['description']), created: text(r['created']), modified: text(r['modified']) };
}

const optionalId = (v: unknown) => (v === null || v === undefined ? null : String(v));
const number = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'bigint' ? Number(v) : Number(v ?? 0));

/** One stored object, with the defaults the desktop's `BoardObject` fills in; an unknown kind is left out, as the desktop can't read it either. */
function objectOf(r: SqlRow): BoardObject | null {
  const kind = text(r['kind']) as BoardObjectKind;
  if (!BOARD_OBJECT_KINDS.includes(kind)) return null;
  const width = number(r['width']);
  const height = number(r['height']);
  const color = text(r['color_hex']);
  const shape = optionalId(r['shape_kind']);
  const data = r['image_data'];
  return {
    id: text(r['id']),
    boardId: text(r['board_id']),
    kind,
    x: number(r['x']),
    y: number(r['y']),
    width: width <= 0 ? 180 : width,
    height: height <= 0 ? 90 : height,
    zIndex: int(r['z_index']),
    text: text(r['text']),
    notes: text(r['notes']),
    colorHex: javaIsBlank(color) ? '#E9E1F5' : color,
    shapeKind: shape === 'RECTANGLE' || shape === 'ELLIPSE' ? shape : null,
    imageMimeType: text(r['image_mime_type']),
    imageData: data instanceof Uint8Array ? data : null,
    linkedEntryId: optionalId(r['linked_entry_id']),
    linkedMapId: optionalId(r['linked_map_id']),
    linkedEventId: optionalId(r['linked_event_id']),
    fromObjectId: optionalId(r['from_object_id']),
    toObjectId: optionalId(r['to_object_id']),
    created: text(r['created']),
    modified: text(r['modified']),
  };
}

function cleanBoard(details: BoardDetails): BoardDetails {
  if (details.name.length > BOARD_LIMITS.name) throw new EntryEditError('invalid', `A name can be at most ${BOARD_LIMITS.name} characters.`);
  if (details.description.length > BOARD_LIMITS.text) throw new EntryEditError('invalid', `A description can be at most ${BOARD_LIMITS.text.toLocaleString('en')} characters.`);
  return { name: javaIsBlank(details.name) ? 'Untitled Board' : details.name.replace(/[\r\n]+/g, ' '), description: details.description };
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
